import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { SalesforceIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/salesforce';
import { FS } from '@dadohr/shared/versions';
import {
  store,
  useMappedLoadingValue
} from '../../../../../../services/firebase';
import {
  callFirebaseFunction,
  useFirebaseFunction
} from '../../../../../../services/firebaseFunctions';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../../services/firecache/documentListener';
import { now } from '../../../../../../services/time';

const toSalesforceConfigDoc = generateToDocFn<SalesforceIntegrationConfig>();

const COLLECTION = FS.salesforceIntegrationConfig;
const collection = () => store().collection(COLLECTION);

const salesforceConfigStore = createDocumentListenerGetter(
  (orgId) => collection().doc(orgId),
  toSalesforceConfigDoc
);

/**
 * Reads the per-org SF integration config doc. Returns an empty default when
 * no doc exists so the integrations context can still report .active=false
 * without flickering. clientSecret + refreshToken on the doc are encrypted at
 * rest — the frontend only inspects .active and identity fields.
 */
export const useSalesforceConfig = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useDocumentListener(salesforceConfigStore(orgId)),
    (d) => {
      if (!d) {
        const n = now();
        const newConfig: Doc<SalesforceIntegrationConfig> = {
          id: orgId,
          collection: COLLECTION,
          data: {
            orgId,
            active: false,
            createdAt: n,
            createdBy: userId,
            editedAt: n,
            editedBy: userId,

            clientId: '',
            clientSecret: '',
            redirectUri: '',
            refreshToken: '',
            accessToken: null,
            accessTokenExpiresAt: null,
            instanceUrl: '',
            loginUrl: '',
            connectedBy: '',
            connectedAt: 0,
            verifiedScopes: [],
            salesforceUserId: '',
            salesforceUserEmail: '',
            salesforceOrganizationId: ''
          }
        };
        return newConfig;
      }
      return d;
    }
  );
};

/**
 * Returned by salesforce_currentUser callable. Mirrors the shape
 * SalesforceCurrentUserResult exported from the salesforce backend package.
 */
export type SalesforceCurrentUser = {
  salesforceUserId: string;
  salesforceUserEmail: string;
  salesforceOrganizationId: string;
  displayName: string;
  instanceUrl: string;
  connectedAt: number;
};

export const useSalesforceCurrentUser = (orgId: string) =>
  useFirebaseFunction<SalesforceCurrentUser>(
    'salesforce_currentUser',
    { orgId },
    [orgId]
  );

/**
 * Persists the customer's Connected App credentials BEFORE redirecting to SF
 * for OAuth. The exchange callable looks them up by orgId at code-redemption
 * time so the clientSecret never travels in the OAuth callback URL.
 */
export const storeSalesforceOAuthCredentials = (input: {
  orgId: string;
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  loginUrl: string;
}) =>
  callFirebaseFunction<{ success: true }>(
    'salesforce_storeOAuthCredentials',
    input
  );

/**
 * Connects via SF Client Credentials Flow — no redirect, no callback URL.
 * Requires the customer's Connected App to have the flow enabled + a Run As
 * user designated. All Dado writes will appear as that Run As user in SF.
 */
export const connectSalesforceViaClientCredentials = (input: {
  orgId: string;
  clientId: string;
  clientSecret: string;
  loginUrl: string;
}) =>
  callFirebaseFunction<{ success: true }>(
    'salesforce_storeClientCredentialsAndConnect',
    input
  );

export const disconnectSalesforce = (orgId: string) =>
  callFirebaseFunction<{ success: true }>('salesforce_disconnect', { orgId });

// ---------------------------------------------------------------------------
// Retry helpers — backing the "Retry" button on the Issues list rows for
// SALESFORCE_CONTACT_RESOLUTION_FAILED / SALESFORCE_WRITE_FAILED. Mirrors the
// email retry contract: "redo with today's data," not "edit the queued op."
// ---------------------------------------------------------------------------

/**
 * Retry a failed Salesforce participant upsert. The BE callable aborts every
 * pending PARTICIPANT_UPSERT op for this experience (optionally narrowed to
 * one templateResourceId), clears the matching instanceErrors, then re-runs
 * the onInstanceCreate hook so fresh ops with fresh template snapshots get
 * published.
 */
export const retrySalesforceParticipantUpsert = (input: {
  orgId: string;
  experienceId: string;
  templateResourceId?: string;
}) =>
  callFirebaseFunction<{ success: true; abortedOpCount: number }>(
    'salesforce_retryParticipantUpsert',
    input
  );

/**
 * Retry failed Salesforce subtask writes. Re-queues every non-completed
 * SALESFORCE subtask op for this experience back to PENDING with zero
 * attempts; the worker dispatcher picks them up on the next sweep using the
 * current SALESFORCE_CONTACT instance entries to resolve the parent record.
 */
export const retrySalesforceSubtaskWrite = (input: {
  orgId: string;
  experienceId: string;
}) =>
  callFirebaseFunction<{ success: true; requeuedOpCount: number }>(
    'salesforce_retrySubtaskWrite',
    input
  );

// ---------------------------------------------------------------------------
// Schema introspection (SF-12) — subtask builder picker uses these to render
// cascading object → field selectors and surface length validation.
// ---------------------------------------------------------------------------

export type SalesforceFieldMeta = {
  name: string;
  label: string;
  type: string;
  length: number | null;
  picklistValues:
    | { value: string; label: string; active: boolean }[]
    | null;
  referenceTo: string[] | null;
  updateable: boolean;
  createable: boolean;
  nillable: boolean;
  // True when the field can be used as the key in an external-ID upsert
  // (PATCH /sobjects/X/<field>/<value>). The SF resource config dialog filters
  // to these for the externalIdField picker.
  idLookup: boolean;
};

export type SalesforceObjectMeta = {
  name: string;
  label: string;
  createable: boolean;
  updateable: boolean;
  fields: SalesforceFieldMeta[];
};

/**
 * Lightweight "list of writable objects" call. No objectNames → backend
 * returns every writable SObject without field detail, perfect for the
 * builder's object dropdown. Pass objectNames for full per-field metadata.
 */
export const useSalesforceObjects = (orgId: string) =>
  useFirebaseFunction<{ objects: SalesforceObjectMeta[] }>(
    'salesforce_describeObjects',
    { orgId },
    [orgId]
  );

/**
 * Returns the writable, non-system fields for a single SObject, sorted by
 * label — used by the builder's field dropdown. Skips the call entirely when
 * objectName is empty so the hook is safe to mount before the admin has
 * picked an object.
 */
export const useSalesforceDataDestinationFields = (
  orgId: string,
  objectName: string
) =>
  useFirebaseFunction<{ fields: SalesforceFieldMeta[] }>(
    'salesforce_getDataDestinationFields',
    objectName ? { orgId, objectName } : undefined,
    [orgId, objectName]
  );

// ---------------------------------------------------------------------------
// List views — powers the user-import "pick which rows" picker. Same shape
// role as picking a tab in Google Sheets.
// ---------------------------------------------------------------------------

export type SalesforceListViewSummary = {
  id: string;
  developerName: string;
  label: string;
  soqlCompatible: boolean;
};

export const useSalesforceListViews = (orgId: string, objectName: string) =>
  useFirebaseFunction<{ listviews: SalesforceListViewSummary[] }>(
    'salesforce_listListViews',
    orgId && objectName ? { orgId, objectName } : undefined,
    [orgId, objectName]
  );

export type SalesforceListViewColumn = {
  fieldNameOrPath: string;
  label: string;
  type: string;
};

export type SalesforceListViewDetail = {
  columns: SalesforceListViewColumn[];
  sampleRows: Array<Record<string, unknown>>;
};

export const useSalesforceListViewColumns = (
  orgId: string,
  objectName: string,
  listViewId: string
) =>
  useFirebaseFunction<SalesforceListViewDetail>(
    'salesforce_getListViewColumns',
    orgId && objectName && listViewId
      ? { orgId, objectName, listViewId }
      : undefined,
    [orgId, objectName, listViewId]
  );
